// src/pages/InvitePatientPage.js
/**
 * Patient‑invite share sheet
 * ───────────────────────────────────────────────────────────
 * Shown immediately after a clinician creates a new patient.
 *
 * Navigation contract (React‑Router `state`):
 * ```ts
 * {
 *   alias:      string; // e.g. "100007" – human‑readable Patient‑ID
 *   inviteLink: string; // relative URL the patient opens to sign up
 * }
 * ```
 *
 * What the clinician can do here
 * • **Scan** – the large `<QRCode/>` is scannable straight from the screen.
 * • **Share** – native Web‑Share sheet (iOS / Android / modern desktop)
 *   opens with a *PNG* snapshot of the QR card **plus** the plain URL so
 *   Messages / WhatsApp / Mail, etc. all work out of the box.
 * • **Download** – fallback that saves the same PNG locally when the
 *   browser doesn’t support file‑sharing.
 * • **Copy link** – 1‑click to clipboard with a 2‑second “Copied!” toast.
 *
 * Under the hood
 * • QR generated by `react‑qr‑code` (zero‑dependency, tiny bundle).
 * • HTML → PNG conversion handled by `html‑to‑image` so we can share /
 *   download the *whole* card (QR + Patient‑ID text) rather than the bare
 *   SVG.
 *
 * Make sure the deps are installed:
 *   npm i react-qr-code html-to-image
 */

import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import QRCode from "react-qr-code"; // real QR component
import * as htmlToImage from "html-to-image";

/* ────────────────────────────────────────────────────────── */
/* Component                                                 */
/* ────────────────────────────────────────────────────────── */
export default function InvitePatientPage() {
  const { state } = useLocation(); // { alias, inviteLink }
  const navigate = useNavigate();

  /* UI state ------------------------------------------------ */
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [downloading, setDl] = useState(false);

  /* Refs ---------------------------------------------------- */
  const cardRef = useRef(null); // the block we turn into a PNG

  /* ── guard: if user hit refresh navigate back ──────────── */
  if (!state?.inviteLink || !state?.alias) {
    navigate("/patients", { replace: true });
    return null;
  }

  const fullUrl = `${window.location.origin}${state.inviteLink}`;

  /* ───────────────────── handlers ────────────────────────── */
  /**
   * Copy the bare URL to clipboard with a tiny UX toast.
   */
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Safari < 15 or browser denied – fall back to prompt
      window.prompt("Copy to clipboard:", fullUrl);
    }
  };

  /**
   * Convert the `cardRef` element → PNG File → open native share sheet.
   * Falls back automatically to URL‑only share if `navigator.canShare`
   * doesn’t allow files.
   */
  const shareCard = async () => {
    if (!navigator.share || sharing || !cardRef.current) return;
    setSharing(true);

    try {
      // 1) DOM → Blob (PNG)
      const blob = await htmlToImage.toBlob(cardRef.current, {
        backgroundColor: "#ffffff", // ensure white background
        pixelRatio: 2, // retina crispness
      });

      let files;
      if (
        blob &&
        navigator.canShare &&
        navigator.canShare({ files: [new File([], "")] })
      ) {
        files = [
          new File([blob], `patient-invite-${state.alias}.png`, {
            type: "image/png",
          }),
        ];
      }

      // 2) open the share sheet
      await navigator.share({
        title: `Invite for patient ${state.alias}`,
        text: "Welcome to pDx – join with the link or QR code below.",
        url: fullUrl,
        ...(files && { files }),
      });
    } catch {
      /* user cancelled or the browser threw – swallow quietly */
    } finally {
      setSharing(false);
    }
  };

  /**
   * Same PNG conversion, but forces a download instead of share – handy on
   * desktop browsers without Web‑Share‑Level‑2 support.
   */
  const downloadPng = async () => {
    if (downloading || !cardRef.current) return;
    setDl(true);
    try {
      const dataUrl = await htmlToImage.toPng(cardRef.current, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `patient-invite-${state.alias}.png`;
      a.click();
    } finally {
      setDl(false);
    }
  };

  const canShare = Boolean(navigator.share);

  /* ───────────────────── JSX ─────────────────────────────── */
  return (
    <div className="w-full h-full flex flex-col items-center gap-4 p-4">
      <h1 className="title-text text-center">Invite Patient</h1>

      {/* ▼ Shareable card (QR + ID) – turned into PNG via html‑to‑image */}
      <div
        ref={cardRef}
        className="bg-container p-4 flex flex-col items-center gap-2"
      >
        <QRCode value={fullUrl} size={256} />
        <p className="subtitle">
          Patient ID: <strong>{state.alias}</strong>
        </p>
      </div>

      {/* ▼ Action buttons */}
      <div className="flex flex-row gap-4">
        {canShare && (
          <button
            onClick={shareCard}
            className="outlined-button w-fit group"
            disabled={sharing}
          >
            {/* upload/share icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-6 h-6 fill-primary group-hover:fill-greyscale-1"
              viewBox="0 -960 960 960"
            >
              <path d="M240-40q-33 0-56.5-23.5T160-120v-440q0-33 23.5-56.5T240-640h120v80H240v440h480v-440H600v-80h120q33 0 56.5 23.5T800-560v440q0 33-23.5 56.5T720-40zm200-280v-447l-64 64-56-57 160-160 160 160-56 57-64-64v447z" />
            </svg>
          </button>
        )}

        {/* download fallback */}
        <button
          onClick={downloadPng}
          className="outlined-button w-fit group"
          disabled={downloading}
        >
          {/* download icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 fill-primary group-hover:fill-greyscale-1"
            viewBox="0 -960 960 960"
          >
            <path d="M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58zM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160z" />
          </svg>
        </button>

        {/* copy‑link */}
        <button
          type="button"
          onClick={copy}
          className="primary-button min-w-[8rem]"
        >
          {copied ? "Copied!" : "Copy link"}
        </button>
      </div>

      {/* plain link for manual copy / reference */}
      <div className="text-center flex flex-col gap-2 max-w-xs break-words">
        <p className="muted-text text-sm">{fullUrl}</p>
      </div>
    </div>
  );
}
